import React, { useState, useEffect } from "react";
import { Modal, Badge, Divider, Typography, Space, message, Select, Row, Col, DatePicker, Input } from "antd";
import { UserAddOutlined, TeamOutlined } from "@ant-design/icons";
import ParticipantSelector from "./ParticipantSelector";
import { createEca, assignParticipants, getDepartamentos, getMunicipios, getSesionEcas } from "../../services/EcasParticipante/ecaParticipanteService";
import dayjs from "dayjs";
const { Title, Paragraph } = Typography;
const { Option } = Select;
export default function CreateECAModal({ isOpen, onClose, onECACreated, user }) {
  const [nombreECA, setNombreECA] = useState(null);
  const [oidNombreECA, setOidNombreECA] = useState(null);
  const [departamento, setDepartamento] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosDisponibles, setMunicipiosDisponibles] = useState([]);
  const [dataEcas, setDataEcas] = useState([]);
  const [fechaRegistro, setFechaRegistro] = useState(dayjs());
  const [estado, setEstado] = useState(1);
  const [tipoEstado, setTipoEstado] = useState([]);
  const [participantesSeleccionados, setParticipantesSeleccionados] = useState([]);
  const [loadingCrear, setLoadingCrear] = useState(false);
  const [conformaciones, setConformaciones] = useState("");

  useEffect(() => {
    if (isOpen) {
      cargarDepartamentos();
      cargarMunicipios();
      cargarEcas();
      setNombreECA(null);
      setDepartamento(null);
      setMunicipio(null);
      setEstado();
      setTipoEstado();
      setFechaRegistro();
      setParticipantesSeleccionados([]);
      setConformaciones("");;
    }
  }, [isOpen]);

  useEffect(() => {
    if (departamento) {
      const filtrados = municipios.filter(m => m.oid_depto === departamento);
      setMunicipiosDisponibles(filtrados);
    } else {
      setMunicipiosDisponibles([]);
    }

    setMunicipio(null);
    setParticipantesSeleccionados([]);
  }, [departamento]);

  const resetFormulario = () => {
    setNombreECA(null);
    setOidNombreECA(null);
    setDepartamento(null);
    setMunicipio(null);
    setMunicipiosDisponibles([]);
    setParticipantesSeleccionados([]);
    setEstado(1);
    setTipoEstado([]);
    setFechaRegistro(dayjs());
    setConformaciones("");
  };

  const cargarDepartamentos = async () => {
    try {
      const res = await getDepartamentos();
      setDepartamentos(res);
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
    }
  };

  const cargarEcas = async () => {
    try {
      const res = await getSesionEcas();
      setDataEcas(res);
    } catch (error) {
      console.error("Error al cargar sesiones ECA:", error);
    }
  }

  const cargarMunicipios = async () => {
    try {
      const res = await getMunicipios();
      setMunicipios(res);
    } catch (error) {
      console.error("Error al cargar municipios:", error);
    }
  };

  const handleCrearECA = async () => {
    if (!nombreECA.trim()) {
      return message.warning("Debes ingresar el nombre de la sesión de la ECA.");
    }
    if (!departamento || !municipio) {
      return message.warning("Debes seleccionar un departamento y municipio.");
    }
    if (participantesSeleccionados.length === 0) {
      return message.warning("Debes seleccionar al menos un participante.");
    }
    if (tipoEstado.length === 0) {
      return message.warning("Debes seleccionar al menos un tipo de temática.");
    }
    setLoadingCrear(true);
    try {
      const res = await createEca({
        oidnombreeca: oidNombreECA,
        nombreeca: nombreECA.trim(),
        observacion: "",
        usercreate: user,
        tipo_estado: tipoEstado.join(","),
        departamento,
        municipio,
        fecha_registro: fechaRegistro.format("YYYY-MM-DD"),
        estado: 1,
        conformaciones: conformaciones.trim(),
      });

      const nuevaECAId = res.oid;
      const participantesIds = participantesSeleccionados.map(p => p.oid);
      await assignParticipants(nuevaECAId, participantesIds);
      message.success("Sesión de la ECA creada y participantes asignados correctamente.");

      const departamentoObj = departamentos.find(d => d.oid === departamento);
      const municipioObj = municipios.find(m => m.oid === municipio);
      onECACreated({
        oid: nuevaECAId,
        nombre: nombreECA.trim(),
        nombreECA: nombreECA.trim(),
        participantes: participantesSeleccionados.length,
        observacion: "",
        estado,
        tipo_estado: tipoEstado,
        fechaCreacion: fechaRegistro.format("YYYY-MM-DD"),
        nombre_departamento: departamentoObj?.departamento || "",
        nombre_municipio: municipioObj?.municipio || "",
        departamento: departamentoObj?.oid || "",
        municipio: municipioObj?.oid || "",
        conformaciones: conformaciones.trim(),
        estadoTexto: estado === 1 ? "Activo" : estado === 2 ? "Inactivo" : "Desconocido",
      });

      resetFormulario();
      onClose();
    } catch (error) {
      console.error("Error al crear ECA:", error);
      message.error("Ocurrió un error al crear la ECA o asignar los participantes.");
    } finally {
      setLoadingCrear(false);
    }
  };

  const handleDepartamentoChange = (nuevoDepartamento) => {
    if (participantesSeleccionados.length > 0) {
      Modal.confirm({
        title: "¿Deseas cambiar el departamento?",
        content: "Se perderá la selección actual de participantes.",
        okText: "Sí, cambiar",
        cancelText: "Cancelar",
        onOk: () => {
          setDepartamento(nuevoDepartamento);
          setMunicipio(null);
          setParticipantesSeleccionados([]);
        },
      });
    } else {
      setDepartamento(nuevoDepartamento);
    }
  };

  return (
    <Modal
      title={<Space><UserAddOutlined /> Crear Nueva Sesión de ECA</Space>}
      open={isOpen}
      onOk={handleCrearECA}
      okText="Crear Sesión ECA"
      cancelText="Cancelar"
      confirmLoading={loadingCrear}
      onCancel={() => {
        resetFormulario();
        onClose();
      }}
      okButtonProps={{
        disabled: !nombreECA || !departamento || !municipio || participantesSeleccionados.length === 0 || !fechaRegistro
      }}
      width={900}
    >
      <Paragraph>
        Crea una nueva Escuela de Campo para Agricultores(as) y asigna participantes al proceso.
      </Paragraph>
      <Row gutter={16}>
        <Col span={24}>
          <Select
            placeholder="Seleccionar Nombre de la Sesión ECA"
            value={oidNombreECA}
            onChange={(oidSeleccionado) => {
              const ecaSeleccionada = dataEcas.find(e => e.oid === oidSeleccionado);
              setOidNombreECA(oidSeleccionado);
              setNombreECA(ecaSeleccionada?.nombre_eca || "");
            }}
            style={{ width: "100%", marginBottom: 15 }}
          >
            {dataEcas.map((eca) => (
              <Option key={eca.oid} value={eca.oid}>{eca.nombre_eca}</Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Select
            mode="multiple"
            placeholder="Seleccionar Tipo de Temática"
            value={tipoEstado}
            onChange={setTipoEstado}
            style={{ width: "100%", marginBottom: 15 }}
          >
            <Option value={1}>Técnico</Option>
            <Option value={2}>Ambiental</Option>
            <Option value={3}>Socioempresarial</Option>
            <Option value={4}>Comercial</Option>
            <Option value={5}>DHAA</Option>
            <Option value={6}>Enfoque Diferencial</Option>
          </Select>
        </Col>
        <Col span={12}>
          <DatePicker
            placeholder="Seleccionar Fecha sesión ECA"
            value={fechaRegistro}
            onChange={(date) => setFechaRegistro(date)}
            style={{ width: "100%", marginBottom: 15 }}
            format="YYYY-MM-DD"
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Select
            placeholder="Seleccionar Departamento"
            style={{ width: "100%", marginBottom: 15 }}
            value={departamento}
            onChange={handleDepartamentoChange}
          >
            {departamentos.map((dep) => (
              <Option key={dep.oid} value={dep.oid}>{dep.departamento}</Option>
            ))}
          </Select>
        </Col>
        <Col span={12}>
          <Select
            placeholder="Seleccionar Municipio"
            style={{ width: "100%", marginBottom: 15 }}
            value={municipio}
            onChange={setMunicipio}
            disabled={!departamento}
          >
            {municipiosDisponibles.map((mun) => (
              <Option key={mun.oid} value={mun.oid}>{mun.municipio}</Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Input.TextArea
            placeholder="Escribe la conformación de la sesión"
            value={conformaciones}
            onChange={(e) => setConformaciones(e.target.value)}
            rows={2}
            style={{ width: "100%", marginBottom: 15 }}
          />
        </Col>
      </Row>
      <Divider style={{ margin: 16 }} />
      {departamento && municipio ? (
        <>
          <Title level={5}>
            <TeamOutlined /> Seleccionar Participantes{" "}
            <Badge count={participantesSeleccionados.length} />
          </Title>
          <ParticipantSelector
            isOpen={isOpen}
            participantesSeleccionados={participantesSeleccionados}
            onParticipantesChange={setParticipantesSeleccionados}
            filtroDepartamentoInicial={departamento}
            filtroMunicipioInicial={municipio}
          />
        </>
      ) : (
        <Typography.Text type="secondary">
          Selecciona primero un departamento y un municipio para visualizar los participantes.
        </Typography.Text>
      )}
    </Modal>
  );
}
